import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    useAddRolePermissionsMutation,
    useAddUserPermissionsMutation,
    useDeleteRolePermissionMutation,
    useDeleteUserPermissionMutation,
    useUpdateRolePermissionsMutation,
    useUpdateUserPermissionsMutation
} from '../../../services/adminApi';
import { enqueueSnackbar } from 'notistack';
import { handleErrors } from '../../../utils/functions';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../redux/slices/appSlice';

type DocumentProps = {
    id: number;
    descripcion: string;
    nombre: string;
    tiposArchivo: { [key: string]: any }[];
};

interface DefaultValues {
    document: DocumentProps | null;
    cargar: boolean;
    descargar: boolean;
    eliminar: boolean;
}

const defaultValues: DefaultValues = {
    document: null,
    cargar: false,
    descargar: false,
    eliminar: false
};

function usePermissions({ handleChangeData, data, drawerAction, selectedItem, scrollView }: any) {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const path = useMemo(() => pathname.match(/[^/]+$/)?.[0], [pathname]);
    const [docTypeSelected, setDocTypeSelected] = useState<DefaultValues>(defaultValues);
    const [rowSelected, setRowSelected] = useState<any>(null);

    //Roles fetchs
    const [deleteRolePermission, { isLoading: deletingRolePermission }] =
        useDeleteRolePermissionMutation();
    const [updateRolePermissions, { isLoading: updatingRolePermission }] =
        useUpdateRolePermissionsMutation();
    const [addRolePermissions, { isLoading: addingRolePermission }] =
        useAddRolePermissionsMutation();

    //Users fetchs
    const [deleteUserPermission, { isLoading: deletingUserPermission }] =
        useDeleteUserPermissionMutation();
    const [addUserPermissions, { isLoading: addingUserPermission }] =
        useAddUserPermissionsMutation();
    const [updateUserPermission, { isLoading: updatingUserPermission }] =
        useUpdateUserPermissionsMutation();

    useEffect(() => {
        return () => setDocTypeSelected(defaultValues);
    }, []);

    const handleChangeDocType = (e: any) => {
        const { name, value, checked, type } = e?.target;

        if (name === 'document' && !value) {
            clearPermissionForm();
            return;
        }
        setDocTypeSelected({
            ...docTypeSelected,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSelectPermission = useCallback((item: any) => {
        setRowSelected(item.row);
        setDocTypeSelected({
            document: item.row.tipoDocumento,
            cargar: item.row.cargar,
            descargar: item.row.descargar,
            eliminar: item.row.eliminar
        });
    }, []);

    const handleSetPermissionData = () => {
        handleChangeData({
            target: {
                name: 'permisos',
                value: [
                    ...data?.permisos,
                    {
                        cargar: docTypeSelected.cargar,
                        descargar: docTypeSelected.descargar,
                        eliminar: docTypeSelected.eliminar,
                        tipoDocumento: docTypeSelected.document,
                        id: docTypeSelected.document?.id
                    }
                ]
            }
        });
        setDocTypeSelected(defaultValues);
        scrollView();
    };

    const handleAddPermission = () => {
        if (drawerAction === 'new') {
            handleSetPermissionData();
        } else {
            const submit = async () => {
                let response: any = null;
                let body: Partial<any> = {
                    idTipoDocumento: docTypeSelected.document!?.id,
                    cargar: docTypeSelected.cargar,
                    descargar: docTypeSelected.descargar,
                    modificar: false,
                    eliminar: docTypeSelected.eliminar
                };

                if (!!rowSelected) {
                    delete body?.idTipoDocumento;

                    response = await (path === 'roles'
                        ? updateRolePermissions
                        : updateUserPermission)({
                        itemId: selectedItem.id,
                        docTypeId: rowSelected.tipoDocumento?.id,
                        body
                    });
                } else {
                    response = await (path === 'roles' ? addRolePermissions : addUserPermissions)({
                        itemId: selectedItem.id,
                        body
                    });
                }

                if (!response.error) {
                    if (!!rowSelected) {
                        const auxData: any = [...(data?.permisos || [])];
                        const index = auxData?.findIndex(
                            (i: any) => i.tipoDocumento?.id === rowSelected.tipoDocumento?.id
                        );
                        const item = auxData?.find(
                            (i: any) => i.tipoDocumento?.id === rowSelected.tipoDocumento?.id
                        );
                        auxData.splice(index, 1, {
                            ...item,
                            cargar: docTypeSelected.cargar,
                            descargar: docTypeSelected.descargar,
                            modificar: false,
                            eliminar: docTypeSelected.eliminar
                        });
                        handleChangeData({
                            target: {
                                name: 'permisos',
                                value: [...auxData]
                            }
                        });
                        scrollView();
                    } else {
                        handleSetPermissionData();
                    }
                    clearPermissionForm();
                    enqueueSnackbar(response?.data?.message || 'Permisos modificados con éxito.', {
                        variant: 'success'
                    });
                } else {
                    handleErrors(response.error);
                }
            };
            dispatch(
                showAlert({
                    title: `¿${rowSelected ? 'Modificar' : 'Agregar'} permiso al ${
                        path === 'roles' ? 'rol' : 'usuario'
                    }? `,
                    keepMounted: true,
                    cancelText: 'Cancelar',
                    confirmText: `Sí, ${rowSelected ? 'modificar' : 'agregar'}`,
                    confirmAction: submit
                })
            );
        }
    };

    const handleDeletePermission = useCallback(
        (item: any) => {
            if (['new', 'edit'].includes(drawerAction) && data?.id) {
                const submit = async () => {
                    const response: any = await (path === 'roles'
                        ? deleteRolePermission
                        : deleteUserPermission)({
                        itemId: selectedItem.id,
                        docTypeId: item.row.id
                    });
                    if (!response.error) {
                        const newPermisos = data?.permisos?.filter(
                            (i: any) => i?.id !== item.row.tipoDocumento?.id
                        );
                        handleChangeData({
                            target: {
                                name: 'permisos',
                                value: newPermisos
                            }
                        });
                        enqueueSnackbar(
                            response?.data?.message || 'Permiso eliminado correctamente.',
                            {
                                variant: 'success'
                            }
                        );
                        clearPermissionForm();
                        setDocTypeSelected(defaultValues);
                        scrollView();
                    } else {
                        handleErrors(response.error);
                    }
                };
                dispatch(
                    showAlert({
                        title: `¿Eliminar permiso del ${path === 'roles' ? 'rol' : 'usuario'}?`,
                        keepMounted: true,
                        cancelText: 'Cancelar',
                        confirmText: 'Sí, eliminar',
                        confirmAction: submit
                    })
                );
            } else {
                const newPermisos = data?.permisos?.filter(
                    (i: any) => i?.id !== item.row.tipoDocumento?.id
                );
                handleChangeData({
                    target: {
                        name: 'permisos',
                        value: newPermisos
                    }
                });
                scrollView();
            }
        },
        // eslint-disable-next-line
        [drawerAction, data]
    );

    const permissionFormValidation = useMemo(() => {
        return (
            !docTypeSelected?.document ||
            (!!docTypeSelected?.document &&
                !docTypeSelected.cargar &&
                !docTypeSelected.descargar &&
                !docTypeSelected.eliminar) ||
            (!!rowSelected &&
                _.isEqual(
                    {
                        cargar: rowSelected.cargar,
                        descargar: rowSelected.descargar,
                        eliminar: rowSelected.eliminar
                    },
                    {
                        cargar: docTypeSelected.cargar,
                        descargar: docTypeSelected.descargar,
                        eliminar: docTypeSelected.eliminar
                    }
                ))
        );
    }, [docTypeSelected, rowSelected]);

    const clearPermissionForm = useCallback(() => {
        setDocTypeSelected(defaultValues);
        setRowSelected(null);
    }, []);

    const deletingPermission = deletingRolePermission || deletingUserPermission;
    const updatingPermission = updatingRolePermission || updatingUserPermission;
    const addingPermission = addingRolePermission || addingUserPermission;

    return {
        rowSelected,
        docTypeSelected,
        addingPermission,
        updatingPermission,
        deletingPermission,
        permissionFormValidation,
        handleAddPermission,
        clearPermissionForm,
        handleChangeDocType,
        handleSelectPermission,
        handleDeletePermission
    };
}

export default usePermissions;
